import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import { useGetBasicDataQuery } from "../../../store/queries/global";
import { getFormatedDate } from "../../../utils/functions/table";
import {
  changeRemittanceStatus,
  changeRemittancePaymentStatus,
  exportRemittance,
} from "./api";
import moment from "moment";
import {
  useGetRemittanceListDataQuery,
  useUpdateRemittanceTableFieldsMutation,
} from "../../../store/queries/remittance";
import { toast } from "react-toastify";
import Select from "react-select";
import { Button } from "@wac-ui-dashboard/wac_component_library";
import { subDays } from "date-fns";
import Style from "./remittance.module.scss";

const useRemittance = ({ subFilter = "" } = {}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showEditModal } = useSelector((state) => state.global);

  const [remittanceState, setRemittanceState] = useState({
    currentPageSize: 10,
    currentPage: 1,
    search: "",
    sortBy: "created_at",
    sortOrder: "desc",
    showNoteForm: false,
    remittanceNoteId: "",
    showStatusModal: false,
    showPaymentStatusModal: false,
    paymentReview: "",
    paymentReviewError: "",
    selectedRemittanceId: "",
    selectedStatus: "",
    selectedPaymentRemittanceId: "",
    selectedPaymentStatus: "",
    showInvoiceModal: false,
    invoiceRemittanceId: "",
    currentBranchFilter: [],
    currentStatusFilter: "",
    start: subDays(new Date(), 30),
    end: new Date(),
  });

  const [date, setDate] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
    key: "selection",
  });

  const { data: basicData = {} } = useGetBasicDataQuery();
  const [updateTableFields] = useUpdateRemittanceTableFieldsMutation();

  const {
    data: mainData = {},
    isFetching,
    isLoading,
    refetch,
  } = useGetRemittanceListDataQuery({
    sort_by: remittanceState.sortBy,
    sort_order: remittanceState.sortOrder,
    search: remittanceState.search,
    page_size: remittanceState.currentPageSize,
    page: remittanceState.currentPage,
    start: getFormatedDate(remittanceState.start),
    end: getFormatedDate(remittanceState.end),
    ...(remittanceState.currentBranchFilter?.length > 0 && {
      branch_id: remittanceState.currentBranchFilter,
    }),
    ...(subFilter !== ""
      ? { status: subFilter === "completed" ? 6 : "" }
      : remittanceState.currentStatusFilter !== "" && {
          status: remittanceState.currentStatusFilter,
        }),
  });

  const paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];

  // Permissions
  const getPermission = (key) => {
    const permission = mainData?.data?.permission?.find((p) =>
      Object.keys(p).includes(key),
    );
    return permission?.[key] ?? 0;
  };

  const hasChangeStatusPermission = getPermission("can_change_status");
  const hasChangePaymentStatusPermission = getPermission(
    "can_change_payment_status",
  );
  const hasAddNotePermission = getPermission("can_add_note");
  const hasExportPermission = getPermission("can_export");

  const handlePagination = (page) => {
    setRemittanceState((prev) => ({ ...prev, currentPage: page }));
  };

  const handlePageSize = ({ value: page_size }) => {
    setRemittanceState((prev) => ({
      ...prev,
      currentPageSize: page_size,
      currentPage: 1,
    }));
  };

  const handleSort = (label) => {
    setRemittanceState((prev) => ({
      ...prev,
      sortBy: label ?? "created_at",
      sortOrder:
        prev.sortBy === label
          ? prev.sortOrder === "asc"
            ? "desc"
            : "asc"
          : "asc",
    }));
  };

  const handleSearch = (e) => {
    setRemittanceState((prev) => ({
      ...prev,
      search: e.target.value,
      currentPage: 1,
    }));
  };

  const handleDateRangeChange = (range) => {
    const startDate = range[0];
    const endDate = range[1];
    setDate({ startDate, endDate });
    setRemittanceState((prev) => ({
      ...prev,
      start: startDate,
      end: endDate,
      currentPage: 1,
    }));
  };

  const handleBranchFilter = (ids) => {
    setRemittanceState((prev) => ({
      ...prev,
      currentBranchFilter: ids,
      currentPage: 1,
    }));
  };

  const handleStatusFilter = (value) => {
    setRemittanceState((prev) => ({
      ...prev,
      currentStatusFilter: value?.value ?? "",
      currentPage: 1,
    }));
  };

  const handleClearClick = () => {
    setRemittanceState((prev) => ({
      ...prev,
      search: "",
      sortBy: "created_at",
      sortOrder: "desc",
      currentPage: 1,
      currentBranchFilter: [],
      currentStatusFilter: "",
      start: subDays(new Date(), 30),
      end: new Date(),
    }));
    setDate({
      startDate: subDays(new Date(), 30),
      endDate: new Date(),
      key: "selection",
    });
  };

  const handleRowClick = (id) => {
    navigate(`/remittance/remittance-details/${id?.[0]}`);
  };

  const handleEditClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = true;
      }),
    );
  };

  const closeEditModal = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = false;
      }),
    );
  };

  const actionOptions = [
    { label: "Edit Columns", value: 0 },
    ...(hasExportPermission ? [{ label: "Export", value: 1 }] : []),
  ];

  const handleActionChange = (item) => {
    if (item?.value === 0) {
      handleEditClick();
    } else if (item?.value === 1) {
      if (mainData?.data?.total_count > 3000) {
        toast.error(
          "Cannot export more than 3000 rows. Please apply filters to reduce results.",
        );
        return;
      }
      exportRemittance({
        sort_by: remittanceState.sortBy,
        sort_order: remittanceState.sortOrder,
        search: remittanceState.search,
        start: getFormatedDate(remittanceState.start),
        end: getFormatedDate(remittanceState.end),
        ...(remittanceState.currentBranchFilter?.length > 0 && {
          branch_id: remittanceState.currentBranchFilter,
        }),
        ...(remittanceState.currentStatusFilter !== "" && {
          status: remittanceState.currentStatusFilter,
        }),
      }).then((response) => {
        let dataType =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        let binaryData = [];
        binaryData.push(response?.data);
        let downloadLink = document.createElement("a");
        downloadLink.href = window.URL.createObjectURL(
          new Blob(binaryData, { type: dataType }),
        );
        downloadLink.setAttribute(
          "download",
          `remittance_${moment(Date.now()).format("MM_DD_YYYY_HH_mm_ss")}.xlsx`,
        );
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
      });
    }
  };

  // Status change
  const handleStatusChange = (data, selectedOption) => {
    const body = {
      remittance_id: data?.id,
      status: selectedOption?.value,
    };

    changeRemittanceStatus(body).then((res) => {
      if (res?.success) {
        toast.success("Status updated successfully");
        refetch();
      } else {
        const validationError = res?.data
          ? Object.values(res.data).flat()[0]
          : null;
        toast.error(
          validationError || res?.message || "Failed to update status",
        );
      }
    });
  };

  // Confirmation modal handlers
  const handleSubmitStatusComplete = () => {
    const body = {
      remittance_id: remittanceState.selectedRemittanceId,
      status: remittanceState.selectedStatus,
    };

    changeRemittanceStatus(body).then((res) => {
      if (res?.success) {
        toast.success("Status updated successfully");
        refetch();
      } else {
        const validationError = res?.data
          ? Object.values(res.data).flat()[0]
          : null;
        toast.error(
          validationError || res?.message || "Failed to update status",
        );
      }
      closeStatusModal();
    });
  };

  const closeStatusModal = () => {
    setRemittanceState((prev) => ({
      ...prev,
      showStatusModal: false,
      selectedRemittanceId: "",
      selectedStatus: "",
    }));
  };

  // Payment status confirmation modal handlers
  const setPaymentReview = (value) => {
    setRemittanceState((prev) => ({
      ...prev,
      paymentReview: value,
      paymentReviewError: "",
    }));
  };

  const handleSubmitPaymentStatusComplete = () => {
    if (remittanceState.paymentReview?.length <= 0) {
      setRemittanceState((prev) => ({
        ...prev,
        paymentReviewError: "Payment Review is required",
      }));
      return;
    }

    const body = {
      remittance_id: remittanceState.selectedPaymentRemittanceId,
      status: remittanceState.selectedPaymentStatus,
      payment_review: remittanceState.paymentReview,
    };

    changeRemittancePaymentStatus(body).then((res) => {
      if (res?.success) {
        toast.success("Payment status updated successfully");
        refetch();
      } else {
        const validationError = res?.data
          ? Object.values(res.data).flat()[0]
          : null;
        toast.error(
          validationError || res?.message || "Failed to update payment status",
        );
      }
      closePaymentStatusModal();
    });
  };

  const closePaymentStatusModal = () => {
    setRemittanceState((prev) => ({
      ...prev,
      showPaymentStatusModal: false,
      selectedPaymentRemittanceId: "",
      selectedPaymentStatus: "",
      paymentReview: "",
      paymentReviewError: "",
    }));
  };

  // Note form handlers
  const handleOpenNoteForm = (remittanceId) => {
    setRemittanceState((prev) => ({
      ...prev,
      showNoteForm: true,
      remittanceNoteId: remittanceId,
    }));
  };

  const handleCloseNoteForm = () => {
    setRemittanceState((prev) => ({
      ...prev,
      showNoteForm: false,
      remittanceNoteId: "",
    }));
  };

  // Invoice handlers
  const handleInvoiceClick = (remittanceId) => {
    setRemittanceState((prev) => ({
      ...prev,
      showInvoiceModal: true,
      invoiceRemittanceId: remittanceId,
    }));
  };

  const closeInvoiceModal = () => {
    setRemittanceState((prev) => ({
      ...prev,
      showInvoiceModal: false,
      invoiceRemittanceId: "",
    }));
  };

  const activeUser = JSON.parse(localStorage.getItem("USER_DETAILS"));
  const isBranchUser = !!activeUser?.branch;

  const branchOptions = useMemo(() => {
    const branches = basicData?.data?.branches || [];
    return [...branches, { _id: "others", name: "Others" }];
  }, [basicData]);

  const remittanceStatusOptions = useMemo(() => {
    const statuses = basicData?.data?.remittance_status || [];
    if (isBranchUser) {
      return statuses.filter((item) => item?.name !== "Incomplete");
    }
    return statuses;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicData]);

  const getStatusLabel = (statusValue) => {
    const found = remittanceStatusOptions.find(
      (item) => item?.value === statusValue,
    );
    return found?.name || statusValue;
  };

  const getStatusBadgeClass = (statusValue) => {
    const label = getStatusLabel(statusValue);
    if (label === "Pending") return "badge-pending-outline";
    if (label === "In Progress") return "badge-inprogress-ouline";
    if (label === "Rate Locked") return "badge-inprogress-ouline";
    if (label === "Completed") return "badge-complete-ouline disabled";
    if (label === "Cancelled") return "badge-cancelled-ouline";
    if (label === "Incomplete") return "badge-incomplete-outline";
    return "";
  };

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
      name: (field, data) => (
        <p
          className="pro-mb-0 pro-pnt"
          onClick={() => navigate(`/user/user-details/${data?.user_id}`)}
        >
          {data[field]}
        </p>
      ),
      remittance_number: (field, data) => (
        <p
          className="pro-mb-0 pro-pnt"
          style={{ color: "var(--pro-primary)", cursor: "pointer" }}
          onClick={() => navigate(`/remittance/remittance-details/${data?.id}`)}
        >
          {data[field]}
        </p>
      ),
      total_amount: (field, data) => (
        <p className="pro-mb-0 pro-text-end">{data[field]}</p>
      ),
      status: (field, data) => {
        const currentStatusVal = remittanceStatusOptions.find(
          (item) => item?.value === data[field],
        );

        return hasChangeStatusPermission ? (
          <Select
            id="status"
            name="status"
            placeholder={"Select"}
            className={`pro-input lg table-ddl pro-badge ${getStatusBadgeClass(
              data[field],
            )}`}
            classNamePrefix="pro-input"
            options={remittanceStatusOptions.filter((option) => {
              const currentStatus = data[field];
              // If current status is one of the intermediate statuses,
              // only show Cancelled, Completed, and the current status
              const intermediateStatuses = [
                "Incomplete",
                "Pending",
                "Rate Locked",
                "In Progress",
              ];
              const currentStatusName = remittanceStatusOptions.find(
                (item) => item?.value === currentStatus,
              )?.name;
              if (intermediateStatuses.includes(currentStatusName)) {
                return (
                  option?.value === currentStatus ||
                  option?.name === "Cancelled" ||
                  option?.name === "Completed"
                );
              }
              return true;
            })}
            getOptionLabel={(option) => option?.name}
            getOptionValue={(option) => option?.value}
            value={currentStatusVal}
            onChange={(selectedOption) => {
              if (selectedOption?.value === 6) {
                setRemittanceState((prev) => ({
                  ...prev,
                  showStatusModal: true,
                  selectedRemittanceId: data?.id,
                  selectedStatus: selectedOption?.value,
                }));
                return;
              }

              handleStatusChange(data, selectedOption);
            }}
            menuPlacement="auto"
            menuPosition="fixed"
            isDisabled={data[field] === 6 || data[field] === 5}
          />
        ) : (
          <p
            className={`pro-mb-0 pro-badge ${getStatusBadgeClass(data[field])}`}
          >
            {currentStatusVal?.name || data[field]}
          </p>
        );
      },
      payment_status: (field, data) => {
        const paymentOptions = basicData?.data?.remittance_payment_status || [];
        const currentPaymentVal = paymentOptions.find(
          (item) => item?.value === data[field],
        );

        const getBadgeClass = (val) => {
          if (currentPaymentVal?.name === "Not Applicable") return "badge-cancelled-ouline";
          if (val === 1) return "badge-complete-ouline";
          if (val === 2) return "badge-inprogress-ouline";
          if (val === 3) return "badge-complete-ouline";
          return "badge-pending-outline";
        };

        return hasChangePaymentStatusPermission ? (
          <Select
            id="payment_status"
            name="payment_status"
            placeholder={"Select"}
            className={`pro-input lg table-ddl pro-badge ${getBadgeClass(
              data[field],
            )}`}
            classNamePrefix="pro-input"
            options={paymentOptions.filter(
              (option) => option?.value === data[field] || option?.value === 1,
            )}
            getOptionLabel={(option) => option?.name}
            getOptionValue={(option) => option?.value}
            value={currentPaymentVal}
            onChange={(selectedOption) => {
              setRemittanceState((prev) => ({
                ...prev,
                showPaymentStatusModal: true,
                selectedPaymentRemittanceId: data?.id,
                selectedPaymentStatus: selectedOption?.value,
              }));
            }}
            menuPlacement="auto"
            menuPosition="fixed"
            isDisabled={
              data[field] === 1 || currentPaymentVal?.name === "Not Applicable"
            }
          />
        ) : (
          <p className={`pro-mb-0 pro-badge ${getBadgeClass(data[field])}`}>
            {currentPaymentVal?.name || data[field]}
          </p>
        );
      },
      note: (field, data) => (
        <p className="pro-mb-0 pro-d-flex pro-justify-between pro-gap-5">
          <span className={`pro-align-self-center ${Style.note_text}`}>
            {data[field]}
          </span>
          {hasAddNotePermission ? (
            <span className="pro-align-self-center">
              <Button
                onClick={() => handleOpenNoteForm(data?.id)}
                className="pro-btn pro-btn-primary min"
              >
                {data[field] ? "Update" : "Add"}
              </Button>
            </span>
          ) : (
            <></>
          )}
        </p>
      ),
      action: (field, data) => {
        const statusLabel = getStatusLabel(data?.status);
        const isDisabled =
          statusLabel === "Incomplete" || statusLabel === "Cancelled";
        return !isDisabled ? (
          <p
            className="pro-mb-0 pro-pnt"
            onClick={() => handleInvoiceClick(data?.id)}
          >
            Summary
          </p>
        ) : (
          <p className="pro-mb-0"> </p>
        );
      },
    };

    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  let newData = { ...mainData?.data?.fields };

  // Dashboard cards config
  const OrderItems = {
    total_orders: {
      label: "total_orders",
      title: "Total Orders",
      icon: "calendar_month",
    },
    today_collection: {
      label: "today_collection",
      title: "Today",
      prev: "Yesterday",
      icon: "event",
    },
    weekly_collection: {
      label: "weekly_collection",
      title: "This week",
      prev: "Last week",
      icon: "date_range",
    },
    monthly_collection: {
      label: "monthly_collection",
      title: "This month",
      prev: "Last month",
      icon: "calendar_month",
    },
    yearly_collection: {
      label: "yearly_collection",
      title: "This Year",
      prev: "Last Year",
      icon: "event_note",
    },
  };

  return {
    mainData,
    remittanceState,
    isFetching,
    isLoading,
    date,
    paginationOptions,
    actionOptions,
    showEditModal,
    newData,
    basicData,
    branchOptions,
    remittanceStatusOptions,
    OrderItems,
    handleSort,
    handleSearch,
    handlePagination,
    handlePageSize,
    handleDateRangeChange,
    handleBranchFilter,
    handleStatusFilter,
    handleClearClick,
    handleEditClick,
    handleActionChange,
    closeEditModal,
    handleCloseNoteForm,
    handleSubmitStatusComplete,
    closeStatusModal,
    handleSubmitPaymentStatusComplete,
    closePaymentStatusModal,
    setPaymentReview,
    closeInvoiceModal,
    getRow,
    refetch,
    updateTableFields,
    handleRowClick,
  };
};

export default useRemittance;
